import type {
  WinnerImportCategories, WinnerImportCategoryDecision, WinnerImportCategoryGroup, WinnerImportCategoryOption,
} from './winner-import.api'

/**
 * What the user answered for one Winner group (SOLY-IMPORT-003 S08): one of the organisation's
 * categories, a category to create under a name, or none for this import.
 */
export type CategoryChoice =
  | { mode: 'category'; categoryId: string }
  | { mode: 'create'; newLabel: string }
  | { mode: 'none' }

export const CREATE_VALUE = '__create__'
export const NONE_VALUE = '__none__'

/** The review starts from the server's proposal for each group. */
export function initialCategoryChoices(categories: WinnerImportCategories | null | undefined): Record<string, CategoryChoice> {
  const choices: Record<string, CategoryChoice> = {}
  for (const group of categories?.groups ?? []) choices[group.key] = proposalOf(group)
  return choices
}

function proposalOf(group: WinnerImportCategoryGroup): CategoryChoice {
  if (group.kind === 'create') return { mode: 'create', newLabel: group.newLabel ?? group.groupName ?? '' }
  if (group.kind === 'none' || !group.categoryId) return { mode: 'none' }
  return { mode: 'category', categoryId: group.categoryId }
}

/** The Select value of a choice. */
export function selectValueOf(choice: CategoryChoice): string {
  if (choice.mode === 'create') return CREATE_VALUE
  if (choice.mode === 'none') return NONE_VALUE
  return choice.categoryId
}

/** The choice a Select value means; « create » keeps the name typed so far, else the group's name. */
export function choiceOf(value: string, group: WinnerImportCategoryGroup, previous: CategoryChoice): CategoryChoice {
  if (value === NONE_VALUE) return { mode: 'none' }
  if (value === CREATE_VALUE)
    return { mode: 'create', newLabel: previous.mode === 'create' ? previous.newLabel : group.newLabel ?? group.groupName ?? '' }
  return { mode: 'category', categoryId: value }
}

/**
 * The categories a group can be filed under: the visible ones, plus a hidden one only when it is the
 * one chosen (said « masquée », never picked by accident).
 */
export function categoryOptionsFor(options: WinnerImportCategoryOption[], choice: CategoryChoice): WinnerImportCategoryOption[] {
  return options.filter((o) => !o.isHidden || (choice.mode === 'category' && choice.categoryId === o.id))
}

/** A « create » answer needs a name; the others are always complete. */
export function isChoiceComplete(choice: CategoryChoice): boolean {
  return choice.mode !== 'create' || choice.newLabel.trim().length > 0
}

/**
 * What the commit sends: every group, as the user left it, saying which ones the review showed
 * mapped (a « create » there is a deliberate change of category).
 */
export function categoryDecisions(
  choices: Record<string, CategoryChoice>,
  categories?: WinnerImportCategories | null,
): WinnerImportCategoryDecision[] {
  const mapped = new Set((categories?.groups ?? []).filter((g) => g.kind === 'mapped').map((g) => g.key))
  return Object.entries(choices).map(([key, choice]): WinnerImportCategoryDecision => {
    const wasMapped = mapped.has(key)
    if (choice.mode === 'category') return { key, mode: 'category', categoryId: choice.categoryId, wasMapped }
    if (choice.mode === 'create') return { key, mode: 'create', newLabel: choice.newLabel.trim(), wasMapped }
    return { key, mode: 'none', wasMapped }
  })
}

/** « 3 nouvelles catégories · 1 groupe sans catégorie », the block's folded line. */
export function categoriesSummary(choices: Record<string, CategoryChoice>, groupCount: number): string {
  const values = Object.values(choices)
  const created = new Set(values.flatMap((c) => (c.mode === 'create' ? [c.newLabel.trim().toLowerCase()] : []))).size
  const none = values.filter((c) => c.mode === 'none').length
  const parts = [`${groupCount} groupe${groupCount > 1 ? 's' : ''} Winner`]
  if (created > 0) parts.push(`${created} nouvelle${created > 1 ? 's' : ''} catégorie${created > 1 ? 's' : ''}`)
  if (none > 0) parts.push(`${none} sans catégorie`)
  return parts.join(' · ')
}
